import { checkPermission } from "@utils/user-permissions";

export const paths = {
  index: "/",
  unauthorized: "/unauthorized",
  vesselCall: {
    index: "vessel-call",
    create: "create",
    vesselScan: "vessel-scan",
    scanResults: "scan-results",
    preArrivalNotification: "pre-arrival-notification",
    imoFAL: "imo-fal",
    additionalDocuments: "additional-documents",
    vesselCallStatus: "vessel-call-status/:id",
    vesselCallPreview: "vessel-call-status", //used in the port services page navigation
    vesselDetails: "vessel-details",
    movementServices: "movement-services",
    addMovementService: "add-movement-service",
    movementService: "movement-service",
    portServices: "port-services",
    operations: {
      index: "operations",
      disputes: "disputes",
      liquidBulk: "liquid-bulk",
      // DBCO-0007: the Dry Bulk tab's path was hardcoded as "dry-bulk" in
      // OperationsContainer with the comment "hardcoded until we will have
      // dry-bulk", because no route existed. The segment is
      // `drybulk-cargo-operations`, matching the feature folder name
      // (BF-DBCO-0002-2, steering Q-F4). NOT `cargo-operations` - that is
      // already taken below by liquid bulk's own sub-tab.
      dryBulk: "drybulk-cargo-operations",
      plda: "plda",
      cargoOperations: "cargo-operations",
    },
    departure: "departure",
    shippingAgencyTransfer: "shipping-agency-transfer",
    addPortService: "add-port-service",
  },
  licenses: {
    index: "license",
    accountIndex: "accounts",
    portOperatorIndex: "port-operator-licenses",
    licenseDetailsById: "license-details/:id",
    licenseDetails: "license-details",
    portOperatorLicenseRequest: "port-operator-license-request",
    vendorLicenseRenewal: "vendor-license-renewal/:renewalId",
    portOperatorLicenseRenewal: "port-operator-license-renewal/:renewalId",
  },
  deficiencies: {
    index: "deficiencies",
    create: "create",
    deficienciesDetails: "deficiency-details/:id",
  },
  portServices: {
    index: "port-services",
  },
  terminalServices: {
    index: "terminal-services",
    fottServices: "fott-services",
    requestFottService: "request-fott-service",
    terminalServiceDetails: "",
    terminalServiceDetailsById: "fott-services/:id",
  },
  vesselManagement: {
    index: "vessel-management",
    vesselStatus: "vessel-status/:id",
    register: "register",
    vesselInformation: "vessel-information",
    vesselType: "vessel-particulars",
    vesselOwnership: "vessel-ownership",
    vesselCertificates: "vessel-certificates",
    additionalDocuments: "additional-documents",
    bunkerBarge: "bunker-barge",
  },
  vesselNomination: {
    index: "vessel-nomination",
    vesselScan: "vessel-scan",
    scanResults: "scan-results",
    preNominationRequest: "pre-nomination-request",
    add: "add",
    addDryBulk: "dry-bulk/add",
    addDryBulkDraft: "dry-bulk/add/:id",
    editDryBulk: "dry-bulk/edit/:id",
    dryBulkDetails: "dry-bulk/:id",
    preArrivalQuestionnaire: "pre-arrival-questionnaire",
    documents: "documents",
    details: "nomination-details/:id",
    paq: "paq",
  },
  movementServices: {
    index: "movement-services",
  },
  portSecurity: {
    index: "port-security",
    clearances: "clearances",
    requestSecurityClearance: "request-security-clearance",
    clearanceForm: "clearance-form",
    personalInfo: "personal-info",
    socialInfo: "social-info",
    occupationInfo: "occupation-info",
    securityClearanceApplication: "security-clearance-application",
    securityClearanceRequestPreview: "security-clearance-request-preview",
    checkId: "check-id",
  },
  gatepass: {
    index: "gatepass",
    gatepasses: "gatepasses",
    gatepassRequestPreview: "gatepass-request-preview",
    requestGatepass: "request-gatepass",
    normalGatepass: "/gatepass/request-gatepass/normal",
    offshoreGatepass: "/gatepass/request-gatepass/offshore",
    truckGatepass: "/gatepass/request-gatepass/truck",
    cargoGatepass: "/gatepass/request-gatepass/cargo",
  },
  // DBCO-0007 (steering Q-F2). The cross-vessel work queue - IA's
  // "Operation Management" page, "primary entry for OPS and Shippers".
  //
  // DBCO-0039 (AD-69): Operations get a top-level "Operation Management" menu entry (`hasDrybulkCargoOperationsAccess`).
  drybulkCargoOperations: {
    index: "drybulk-cargo-operations",
    add: "add",
    importDeferred: "add/import-deferred",
  },
  bayHire: {
    index: "bay-hire",
    operationsQueue: "bay-hire/operations",
    // DFT-0015: manual waiting-list view, sibling to the Ongoing queue.
    waitingList: "bay-hire/operations/waiting-list",
    // Megaplan (2026-08-05, Operations 3-tab restructure): resolved/terminal
    // requests (APPROVED/REJECTED/CANCELLED), sibling to the Ongoing queue
    // and the Waiting list.
    history: "bay-hire/operations/history",
    // DFT-0027 (Slice B1): dedicated full-page vendor request detail route,
    // mirroring paths.licenses.licenseDetailsById/licenseDetails.
    requestDetail: "bay-hire/request-details/:id",
    requestDetailBase: "bay-hire/request-details",
    // DFT-0096 (2026-09-02): deliberately no `:id` variant - create/resubmit/
    // update/renew all share this one URL; which existing request (if any) is
    // being acted on comes from the persisted useBayHireRequestFormStore, not
    // a route param. See frontend-guidelines.md Section 1's carve-out.
    request: "bay-hire/request",
  },
  notifications: "notifications",
  settings: "settings",
  vendorLicenseRequest: "/marhub-account-request",
  vendorLicenseRequestConfirmation: "/marhub-account-request/confirmation",
  checkSecurityClearance: "/check-security-clearance",
};

export const AppNavigationItems = (
  userAccessPermissions: string[],
  location: string,
  customPath: string | null,
) => {
  const isLicensesActive =
    location?.includes(paths.licenses.index) ||
    location?.includes(paths.licenses.accountIndex) ||
    location?.includes(paths.licenses.portOperatorIndex);
  const hasFottServiceAccess = checkPermission(userAccessPermissions, "hasFottServiceAccess");

  return [
    {
      title: "Dashboard ",
      path: "/",
      testId: "dashboard-button",
      isVisible: false,
      isActive: null,
    },
    {
      title: "Vessel Call Management",
      path: paths.vesselManagement.index,
      isActive:
        location?.includes(paths.vesselCall.index) ||
        location?.includes(paths.vesselManagement.index) ||
        location?.includes(paths.deficiencies.index) ||
        location?.includes(paths.portServices.index) ||
        location?.includes(paths.movementServices.index) ||
        location?.includes(paths.vesselNomination.index),
      isVisible:
        checkPermission(userAccessPermissions, "hasVesselRegistrationAccess") ||
        checkPermission(userAccessPermissions, "hasVesselCallAccess") ||
        checkPermission(userAccessPermissions, "hasFottVesselNominationAccess") ||
        checkPermission(userAccessPermissions, "hasDryBulkVesselNominationAccess"),
      testId: "vesselManagement-button",
      submenu: [
        {
          title: "Vessel Management",
          path: paths.vesselManagement.index,
          testId: "vesselManagement-submenuButton",
          isActive: null,
          isVisible: checkPermission(userAccessPermissions, "hasVesselRegistrationAccess"),
        },
        {
          title: "Vessel Deficiencies",
          path: paths.deficiencies.index,
          testId: "deficiencies-button",
          isActive: location?.includes(paths.deficiencies.index),
          isVisible: checkPermission(userAccessPermissions, "hasFottDeficiencyAccess"),
        },
        {
          title: "Vessel Nomination",
          path: paths.vesselNomination.index,
          testId: "vesselNomination-submenuButton",
          isActive: null,
          isVisible:
            checkPermission(userAccessPermissions, "hasFottVesselNominationAccess") ||
            checkPermission(userAccessPermissions, "hasDryBulkVesselNominationAccess"),
        },
        {
          title: "Vessel Call",
          path: paths.vesselCall.index,
          testId: "vesselCall-submenuButton",
          isActive: null,
          isVisible: checkPermission(userAccessPermissions, "hasVesselCallAccess"),
        },
        {
          title: "Port Services",
          path: paths.portServices.index,
          isActive: null,
          testId: "portServices-button",
          isVisible: checkPermission(userAccessPermissions, "hasVesselCallAccess"), //to add the right permissions
        },
        {
          title: "Movement Services",
          path: paths.movementServices.index,
          testId: "movementServices-submenuButton",
          isActive: null,
          isVisible: checkPermission(userAccessPermissions, "hasMovementServiceAccess"),
        },
      ],
    },
    {
      title: "Services",
      path: `${paths.terminalServices.index}/${paths.terminalServices.fottServices}`,
      isActive: location?.includes(paths.terminalServices.index),
      isVisible: hasFottServiceAccess,
      testId: "services-button",
      submenu: [
        {
          title: "FOTT services",
          path: `${paths.terminalServices.index}/${paths.terminalServices.fottServices}`,
          testId: "fottServices-submenuButton",
          isActive: location?.includes(paths.terminalServices.fottServices),
          isVisible: hasFottServiceAccess,
        },
      ],
    },
    {
      title: "Security Clearance",
      isActive: location?.includes(paths.portSecurity.index),
      path: customPath || paths.portSecurity.index,
      testId: "portSecurity-button",
      isVisible:
        checkPermission(userAccessPermissions, "hasSecurityClearanceAccess") ||
        checkPermission(userAccessPermissions, "hasSecurityClearanceRequestAccess"),
    },
    {
      title: "Gatepass",
      path: paths.gatepass.index,
      isActive: location?.includes(paths.gatepass.index),

      testId: "gatepass-button",
      isVisible: checkPermission(userAccessPermissions, "hasGatepassAccess"),
    },
    {
      title: "Bay Hire",
      path: paths.bayHire.index,
      isActive: location?.includes(paths.bayHire.index),
      testId: "bayHire-button",
      isVisible: checkPermission(userAccessPermissions, "hasBayHireAccess"),
    },
    {
      title: "Operation Management",
      path: paths.drybulkCargoOperations.index,
      isActive: location?.includes(paths.drybulkCargoOperations.index),
      testId: "operationManagement-button",
      isVisible: checkPermission(userAccessPermissions, "hasDrybulkCargoOperationsAccess"),
      submenu: [
        {
          title: "Dry Bulk",
          path: paths.drybulkCargoOperations.index,
          testId: "dryBulkOperations-submenuButton",
          isActive: null,
          isVisible: checkPermission(userAccessPermissions, "hasDrybulkCargoOperationsAccess"),
        },
      ],
    },
    {
      title: "Licenses",
      path: paths.licenses.index,
      testId: "licenses-button",
      isActive: isLicensesActive,
      isVisible:
        checkPermission(userAccessPermissions, "hasCustomerLicenseAccess") &&
        checkPermission(userAccessPermissions, "hasPortOperatorLicenseAccess") &&
        checkPermission(userAccessPermissions, "hasVendorLicenseAccess"),
    },
  ];
};
